// @flow strict
import { DatePicker, Divider, Form, message, Spin } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriberRemaining, updateSubscriberRemaining } from '../../../api/pricing';
import FormComponent from '../../../component/common/FormComponent';
dayjs.extend(customParseFormat);

function UpdateSubscriberInfo({ subscriber, setIsOpenUpdateModal }) {
  const [remaining, setRemaining] = useState(null);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expireDate, setExpireDate] = useState(null);
  const navigate = useNavigate();

  const subscriberRemaining = useCallback(
    async () => {
      setIsDataFetching(true);

      try {
        const res = await getSubscriberRemaining(subscriber.company._id);
        setRemaining(res.data.remaining);
        setExpireDate(res.data.expireDate);
      } catch (error) {
        console.log(error);
      } finally {
        setIsDataFetching(false);
      }
    }, [subscriber.company._id]);

  const handleUpdateData = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        expireDate: expireDate,
      };

      const res = await updateSubscriberRemaining(subscriber.company._id, payload);
      message.success(res.data.message);
      setLoading(false);
      setRemaining(res.data.remaining);
      setIsOpenUpdateModal(false);
      navigate(0);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const getDefaultValue = () => {
    return {
      token: remaining?.token,
      assistant: remaining?.assistant,
      user: remaining?.user,
      chat_providers: remaining?.chat_providers,
      max_file: remaining?.max_file,
      advance_parameter: remaining?.advance_parameter,
      customize_chat: remaining?.customize_chat,
    };
  };

  const formItems = [
    {
      name: "token",
      label: "Chat Token",
      type: "number",
      rules: [
        {
          required: true,
          message: "Chat token is required!",
        },
      ],
    },
    {
      name: "assistant",
      label: "Assistant Limit",
      type: "number",
      rules: [
        {
          required: true,
          message: "Assistant limit is required!",
        },
      ],
    },
    {
      name: "chat_providers",
      label: "Chat Providers",
      type: "multiselect",
      options: ['openai', 'gemini', 'claude'].map((item) => ({
        label: item,
        value: item,
      })),
      rules: [
        {
          required: true,
          message: "At least one chat provider is required!",
        },
      ],
    },
    {
      name: "max_file",
      label: "Max File Size (MB)",
      type: "number",
      rules: [
        {
          required: true,
          message: "Maximum file size is required!",
        },
      ],
    },
    {
      name: "user",
      label: "User Limit",
      type: "number",
      rules: [
        {
          required: true,
          message: "User limit is required!",
        },
      ],
    },
    {
      name: "advance_parameter",
      label: "Access to Advance Parameter Settings",
      type: "checkbox",
      rules: [
        {
          required: true,
          message: "Advance parameter option is required!",
        },
      ],
    },
    {
      name: "customize_chat",
      label: "Access to Customize Chat.",
      type: "checkbox",
      rules: [
        {
          required: true,
          message: "Customize chat option is required!",
        },
      ],
    },
    {
      name: "submitreset",
      submitlabel: "Update Subscription",
      resetlabel: "Reset",
      type: "submitreset",
      className: "m-1",
      loading: loading
    },
  ];

  const disabledDate = (current) => {
    // Can not select days before today and today
    return current && current < dayjs().endOf('day');
  };

  useEffect(() => {
    subscriberRemaining()
  }, [subscriberRemaining]);

  return (
    <div className="">
      <Spin spinning={isDataFetching} tip="Loading...">
        <Divider />
        {
          expireDate &&
          <Form.Item
            label="Current Cycle Expire"
            name="expireDate"
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please select an expire date!',
              },
            ]}
          >
            <DatePicker
              picker="date"
              className="w-100"
              onChange={(_, date) => setExpireDate(date)}
              defaultValue={dayjs(expireDate, 'YYYY-MM-DD')}
              disabledDate={disabledDate}
            />
          </Form.Item>
        }
        <FormComponent
          formItems={formItems}
          handleSubmit={handleUpdateData}
          layout="vertical"
          className="form"
          defaultValues={getDefaultValue()}
        />
      </Spin>
    </div>
  );
};

export default UpdateSubscriberInfo;